import { useCanViewEmails } from '../../../helpers/useCanViewEmails';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import cx from 'classnames';
import { useOnClickOutside } from 'usehooks-ts';
import { createPortal } from 'react-dom';

import { getSubGroupsByGroupIdThunk } from '../../../store/subGroups/subGroupsSlice';
import EditSubGroupModal from '../../SubGroupManagementGeneralView/EditSubGroupModal';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { SUBGROUP_LIST_ITEM, USER } from '../../../enums/TYPES';
import { patchSubGroupAnalysts } from '../../../api';
import { DateFormatter } from '../../../helpers';
import styles from './SubGroupCard.module.scss';
import { NOTIFICATION } from '../../../enums';
import { ROLES } from '../../../enums/ROLES';
import PeopleCount from '../PeopleCount';
import TrashButton from '../TrashButton';
import EditButton from '../EditButton';
import { MoreIcon } from '../../../assets/icons/threedots';

type TSubGroupCard = {
  subgroup: SUBGROUP_LIST_ITEM;
  toDelete?: (id: string) => void;
  managementList?: USER[];
  viewersList?: USER[];
  width: number;
  onSelect: (sub: SUBGROUP_LIST_ITEM) => void;
  selectedSubgroup: SUBGROUP_LIST_ITEM | null;
};

const SubGroupCard: React.FC<TSubGroupCard> = ({
  subgroup,
  toDelete,
  managementList = [],
  viewersList = [],
  width,
  onSelect,
  selectedSubgroup,
}) => {
  const canViewEmails = useCanViewEmails();
  const [allUsersList, setAllUsersList] = useState([...subgroup.analysts, ...subgroup.viewers]);
  const { sort, sortOption } = useAppSelector((state) => state.subGroupsStore);
  const user = useAppSelector((state) => state.user);

  const [showEditModal, setShowEditModal] = useState(false);
  const [hover, setHover] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(subgroup.title);
  const ref = useRef<HTMLDivElement | null>(null);

  const notification = useSnackbar();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const [showDotsMenu, setShowDotsMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  useOnClickOutside(actionMenuRef, (e) => {
    e.stopPropagation();
    setShowDotsMenu(false);
  });

  useEffect(() => {
    const checkIfClickedOutside = (e: MouseEvent) => {
      if (isEditing && ref.current && !ref.current.contains(e.target as Node)) {
        setIsEditing(false);
        setHover(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isEditing]);

  useEffect(() => {
    setAllUsersList([...subgroup.analysts, ...subgroup.viewers]);
  }, [subgroup]);

  const onDelete = (e?: any) => {
    if (e) {
      e?.stopPropagation();
    }
    toDelete && toDelete(subgroup.id);
  };

  const switchEditModal = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setShowEditModal(!showEditModal);
  };

  const handleTitleSubmit = async (e: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    e.preventDefault();

    if (title === subgroup.title) {
      setHover(false);
      setIsEditing(false);
      return;
    } else if (title.trim().length === 0) {
      setTimeout(() => {
        notification.showMessage(NOTIFICATION.EMPTY);
      }, 100);

      setTitle(subgroup.title);
      setHover(false);
      setIsEditing(false);
      return;
    }

    setIsEditing(false);

    try {
      await patchSubGroupAnalysts(subgroup.id, {
        title,
        groupId: location.state as string,
      });
      dispatch(getSubGroupsByGroupIdThunk({ groupId: location.state as string, sort, sortOption }));
      notification.showMessage(NOTIFICATION.UPDATED);

      setHover(false);
    } catch (error) {
      // @ts-ignore
      if (err?.response?.data?.message) {
        // @ts-ignore
        notification.showMessage(err.response.data.message);
      } else {
        notification.showMessage(NOTIFICATION.SOMETHING_WENT_WRONG);
      }
      console.error(error);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleTitleEdit = () => {
    setIsEditing(true);
  };

  const onRedirect = (e: React.MouseEvent<HTMLDivElement>) => {
    if (showEditModal) return;
    onSelect(subgroup);
  };

  const isSubgroupSelected = useMemo(() => {
    return selectedSubgroup && selectedSubgroup.id === subgroup.id;
  }, [selectedSubgroup, subgroup.id]);

  const switchDotsMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (showDotsMenu) {
      setShowDotsMenu(false);
      return;
    }

    const menuWidth = actionMenuRef.current?.offsetWidth || 190;
    const menuHeight = actionMenuRef.current?.offsetHeight || 200;
    const posX = Math.min(Math.max(e.clientX - menuWidth, 8), window.innerWidth - menuWidth - 8);
    const posY = Math.min(Math.max(e.clientY, 8), window.innerHeight - menuHeight - 8);

    setMenuPosition({
      x: posX,
      y: posY,
    });

    setShowDotsMenu(true);
  };

  return (
    <div
      onClick={onRedirect}
      onContextMenu={switchDotsMenu}
      className={cx(styles.card, isSubgroupSelected ? styles.selectedSubgroup : '')}
      ref={ref}
      style={{ position: 'relative' }}
    >
      <div onClick={onRedirect} className={styles.title}>
        {user.role === ROLES.ORGANIZATION_ADMIN ? (
          <>
            {isEditing ? (
              <form
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onSubmit={handleTitleSubmit}
              >
                <input className={styles.input} onChange={handleTitleChange} defaultValue={subgroup.title} onBlur={handleTitleEdit} />
              </form>
            ) : (
              <div>
                <span onClick={onRedirect}>{subgroup.title}</span>
              </div>
            )}
          </>
        ) : (
          <div>{subgroup.title}</div>
        )}
      </div>

      <div onClick={onRedirect} className={cx(styles.details, width < 680 ? styles.miniWidth : '')}>
        <Tooltip
          title={
            <div className={styles.tooltip} style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {allUsersList.map((u) => (
                <div key={`subgroup_tooltip_${u.id}`} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ marginRight: '16px' }}>{u?.username ? u.username : (canViewEmails ? u.email : 'User')}</span>
                  <span>{u.role.title.replace('Organization', 'Organisation')}</span>
                </div>
              ))}
            </div>
          }
          arrow
          placement="right"
        >
          <div style={{ width: '60px' }}>
            <PeopleCount count={allUsersList.length} />
          </div>
        </Tooltip>
      </div>

      {width > 680 && <div className={styles.created}>{DateFormatter(subgroup.created_at, true)}</div>}
      {width > 680 && <div className={styles.updated}>{DateFormatter(subgroup.updated_at!, true)}</div>}

      <div className={cx(styles.action, width < 680 ? styles.miniWidthAction : '')}>
        {user.role === ROLES.ORGANIZATION_ADMIN && (
          <div className={styles.dotsMenuBtn} onClick={switchDotsMenu}>
            <div className={styles.dotIcon}>
              <MoreIcon />
            </div>
          </div>
        )}
      </div>

      {user.role === ROLES.ORGANIZATION_ADMIN &&
        createPortal(
          <div
            ref={actionMenuRef}
            className={styles.actionMenu}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              display: showDotsMenu ? 'block' : 'none',
              top: `${menuPosition.y}px`,
              left: `${menuPosition.x}px`,
            }}
          >
            <div
              className={styles.menuOption}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDotsMenu(false);
                setIsEditing(true);
              }}
            >
              <EditButton />
              <span>Rename</span>
            </div>

            <div
              className={styles.menuOption}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDotsMenu(false);
                switchEditModal();
              }}
            >
              <EditButton />
              <span>Edit</span>
            </div>

            <div
              className={styles.menuOption}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDotsMenu(false);
                onDelete(e);
              }}
            >
              <TrashButton />
              <span>Delete</span>
            </div>
          </div>,
          document.body,
        )}

      <EditSubGroupModal
        group={subgroup}
        open={showEditModal}
        switchModal={switchEditModal}
        viewersList={viewersList}
        managementList={managementList}
      />
    </div>
  );
};

export default SubGroupCard;
